import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    ElementRef,
    OnDestroy,
    TemplateRef,
    ViewChild,
    computed,
    effect,
    inject,
    signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import moment from 'moment';
import { Observable, forkJoin, from as from$, of } from 'rxjs';
import { catchError, concatMap, map, mergeMap, toArray } from 'rxjs/operators';
import { Workbook } from 'exceljs';
import FileSaver from 'file-saver';
import { exportDataGrid } from 'devextreme/excel_exporter';
import { sumBy as _sumBy } from 'lodash';

import { DxDataGridComponent } from 'devextreme-angular';
import { DxDataGridComponent as DxDataGrid } from 'devextreme-angular/ui/data-grid';
import { DxSelectBoxComponent } from 'devextreme-angular/ui/select-box';
import { DxTagBoxComponent } from 'devextreme-angular/ui/tag-box';
import { DxButtonComponent } from 'devextreme-angular/ui/button';
import { DxDateBoxComponent } from 'devextreme-angular/ui/date-box';
import {
    DxiColumnComponent,
    DxoColumnChooserComponent,
    DxoFilterRowComponent,
    DxoGroupPanelComponent,
    DxoGroupingComponent,
    DxoHeaderFilterComponent,
    DxoPagerComponent,
    DxoPagingComponent,
    DxoSummaryComponent,
    DxiTotalItemComponent,
    DxiGroupItemComponent,
} from 'devextreme-angular/ui/nested';
import { DxTemplateDirective } from 'devextreme-angular/core';
import { DatePipe, NgTemplateOutlet } from '@angular/common';

import {
    HeaderContentService,
    MessageType,
    NotificationService,
    UtilService,
} from 'src/app/shared/services';
import {
    FinanceEntityRollup,
    FinancePeriodQuery,
    FinanceStoreRollup,
    FinanceSummary,
    FinanceTxService,
    FinanceUpsertResult,
} from 'src/app/shared/services/finance/finance-tx.service';
import {
    FinanceSyncService,
    FinanceSyncStoreReport,
    FinanceVerifyReport,
} from 'src/app/shared/services/finance/finance-sync.service';
import {
    FinanceOrderRecord,
    PeriodBasis,
    round2,
} from 'src/app/shared/services/finance/finance-record';
import {
    ALL_ENTITIES,
    VAT_THRESHOLD_THB,
    VAT_THRESHOLD_WARN_THB,
} from 'src/app/shared/config/entity-config';
import { EntityId, STORES, StoreId } from 'src/app/shared/config/store-config';
import { FormatNumberPipe } from 'src/app/shared/pipes';
import {
    MonthRevenue,
    YearEndProjection,
    monthKey,
    projectYearEnd,
} from './threshold-projection';

interface ThresholdRow {
    entityId: EntityId;
    legalName: string;
    vatRegistered: boolean;
    revenue: number;
    // Year-end projection — see threshold-projection.ts for the basis, and
    // for why it is no longer "÷ current month number × 12".
    projected: number;
    // Revenue less what was refunded to buyers, shown beside `revenue` for
    // the reader; the bar, state and projection stay on the before-returns
    // figure. null when the Lambda sent no refund total.
    afterRefunds: number | null;
    // What `projected` was taken from, for the label beside it.
    projectionNote: string;
    // The year's months so far, so a partly synced month is visible instead
    // of silently averaged in. Empty when the Lambda sent no split.
    months: ThresholdMonth[];
    pctOfThreshold: number;
    state: 'ok' | 'warn' | 'over';
}

interface ThresholdMonth {
    key: string; // YYYY-MM
    label: string;
    value: number;
    // Left out of the projection as partially synced.
    partial: boolean;
}

interface BasisGap {
    storeId: string;
    stored: number;
    hidden: number;
}

interface VerifyRow {
    storeId: string;
    title: string;
    stored: number;
    platform: number | null;
    storedMoney: number;
    platformMoney: number | null;
    state: 'ok' | 'short' | 'over' | 'unknown' | 'error';
    detail: string;
}

// 'unknown' does not count against the verdict — it means the platform
// offers no like-for-like figure, not that something is wrong. Only a
// genuine mismatch or a failed probe is a failure. The panel title and the
// toast both ask this, so they cannot disagree.
const verifyRowPasses = (row: VerifyRow): boolean =>
    row.state === 'ok' || row.state === 'unknown';

const THAI_MONTH_ABBR = [
    'ม.ค.',
    'ก.พ.',
    'มี.ค.',
    'เม.ย.',
    'พ.ค.',
    'มิ.ย.',
    'ก.ค.',
    'ส.ค.',
    'ก.ย.',
    'ต.ค.',
    'พ.ย.',
    'ธ.ค.',
];

const monthAbbr = (key: string): string =>
    THAI_MONTH_ABBR[Number(key.slice(5)) - 1] ?? key;

interface YearMonth {
    year: number;
    month: number; // 1-12
}

const monthLabel = (ym: YearMonth): string =>
    `${THAI_MONTH_ABBR[ym.month - 1]} ${ym.year}`;

// A sync pulls each month in turn and a month is minutes, so a range is
// capped rather than left open-ended: a mis-dragged picker must not start a
// three-year pull.
const SYNC_MAX_MONTHS = 12;

// The label beside "คาดทั้งปี": what the figure was projected from, so a
// number built on seven months is never read as one built on nine.
const projectionNote = (p: YearEndProjection): string => {
    if (p.basis === 'closed-year') {
        return '';
    }
    if (p.basis === 'elapsed-days') {
        return 'ตามสัดส่วนวันที่ผ่านมาของปี';
    }
    const note = 'เฉลี่ย ' + p.monthsUsed + ' เดือนเต็ม × 12';
    if (p.partialMonths.length === 0) {
        return note;
    }
    return (
        note +
        ' · ' +
        p.partialMonths.map(monthAbbr).join(' ') +
        ' ยอดต่ำผิดปกติ จึงไม่นับ — ควรตรวจสอบ'
    );
};

// Every month of the year up to the current one (all twelve for a past
// year), zero-filled, so a gap reads as a gap.
const monthFigures = (
    year: number,
    months: readonly MonthRevenue[] | undefined,
    p: YearEndProjection,
    today = new Date(),
): ThresholdMonth[] => {
    if (!months) {
        return [];
    }
    const byMonth = new Map(
        months.map((m) => [m.month, m.taxableConsideration ?? m.buyerTotal]),
    );
    const partial = new Set(p.partialMonths);
    const shown =
        year < today.getFullYear()
            ? 12
            : year > today.getFullYear()
              ? 0
              : today.getMonth() + 1;
    return Array.from({ length: shown }, (_, i) => {
        const key = monthKey(year, i + 1);
        return {
            key,
            label: THAI_MONTH_ABBR[i],
            value: byMonth.get(key) ?? 0,
            partial: partial.has(key),
        };
    });
};

// Money columns need an explicit Excel number format. Cell templates only
// affect what is drawn on screen, so without this the raw float lands in the
// .xlsx and an accountant opens 271.99999999999994. Same approach as
// reordering.component.ts.
const EXPORT_NUM_FORMATS: Record<string, string> = {
    'gross.taxableConsideration': '#,##0.00',
    'gross.buyerTotal': '#,##0.00',
    'gross.sellerRevenue': '#,##0.00',
    'gross.sellerDiscount': '#,##0.00',
    'gross.platformSubsidy': '#,##0.00',
    'fees.commission': '#,##0.00',
    'fees.transaction': '#,##0.00',
    'fees.service': '#,##0.00',
    'fees.affiliate': '#,##0.00',
    'fees.shippingBorne': '#,##0.00',
    'fees.other': '#,##0.00',
    'fees.total': '#,##0.00',
    netSettlement: '#,##0.00',
    'vat.base': '#,##0.00',
    'vat.output': '#,##0.00',
    'feeVat.input': '#,##0.00',
};

// The width the page's controls need on one line, as the header row would
// lay them out. The class is added for this one synchronous read and removed
// before the browser paints.
function oneLineWidth(menu: HTMLElement): number {
    menu.classList.add('measuring');
    const width = menu.getBoundingClientRect().width;
    menu.classList.remove('measuring');
    return Math.ceil(width);
}

@Component({
    selector: 'app-revenue-fees',
    templateUrl: './revenue-fees.component.html',
    styleUrls: ['./revenue-fees.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        DxSelectBoxComponent,
        DxTagBoxComponent,
        DxButtonComponent,
        DxDateBoxComponent,
        DxDataGrid,
        DxiColumnComponent,
        DxoColumnChooserComponent,
        DxoFilterRowComponent,
        DxoGroupPanelComponent,
        DxoGroupingComponent,
        DxoHeaderFilterComponent,
        DxoPagerComponent,
        DxoPagingComponent,
        DxoSummaryComponent,
        DxiTotalItemComponent,
        DxiGroupItemComponent,
        DxTemplateDirective,
        FormatNumberPipe,
        DatePipe,
        NgTemplateOutlet,
    ],
})
export class RevenueFeesComponent implements AfterViewInit, OnDestroy {
    private cdref = inject(ChangeDetectorRef);
    private destroyRef = inject(DestroyRef);
    private financeTxService = inject(FinanceTxService);
    private financeSyncService = inject(FinanceSyncService);
    private notificationService = inject(NotificationService);
    private utilService = inject(UtilService);
    private headerContentService = inject(HeaderContentService);

    @ViewChild('dataGrid', { static: true }) dataGrid!: DxDataGridComponent;
    @ViewChild('headerMenu', { static: true })
    private headerMenu!: TemplateRef<unknown>;

    // The controls go in the app header only when they fit there on one
    // line; otherwise they render at the top of the page. The header row is a
    // fixed height, so wrapping inside it spilled the second line over the
    // KPI tiles. Measured in the page — where they always render first — and
    // never in the header, so the two places cannot disagree by a pixel and
    // bounce the menu between them.
    private readonly menuWidth = signal(0);
    readonly menuInHeader = computed(() => {
        const width = this.menuWidth();
        return width > 0 && width <= this.headerContentService.slotWidth();
    });

    @ViewChild('inPageMenu')
    private set inPageMenu(ref: ElementRef<HTMLElement> | undefined) {
        const menu = ref?.nativeElement.querySelector<HTMLElement>(
            '.header-page-actions',
        );
        if (menu) {
            this.menuWidth.set(oneLineWidth(menu));
        }
    }

    storeSelection = STORES.map((store) => ({
        id: store.storeId,
        text: store.title,
    }));
    entitySelection = ALL_ENTITIES.map((entity) => ({
        id: entity.id,
        text: entity.legalName,
    }));

    // Settlement is the default because it is the only date EVERY row has.
    //
    // Tax point is the more correct basis for ภ.พ.30 and stays available, but
    // no finance API returns a ship date: only Shopee has one, joined from
    // pickup_done_time on the order. Defaulting to it hid 3 of 5 stores and
    // 46% of rows behind an empty grid that looked like "no orders" rather
    // than "this basis has no data for these stores" — which is how it was
    // first reported. A default that silently omits half the money is worse
    // than a default that is merely less precise, so the honest one wins and
    // basisGaps below says out loud what any basis is leaving out.
    //
    // Since e322946 (2026-08-05) the tax point is order creation on every
    // platform, so that gap now only covers rows synced before then; the
    // default stays settlement because it is the one date every row is
    // guaranteed to carry.
    periodBasisSelection: { id: PeriodBasis; text: string }[] = [
        { id: 'settlementDate', text: 'เงินเข้า (settlement) — ครบทุกร้าน' },
        { id: 'orderDate', text: 'วันสั่งซื้อ (order date)' },
        { id: 'taxPointDate', text: 'Tax point — สำหรับ ภ.พ.30' },
    ];

    // The period is a run of whole months, held as the first day of the
    // first and last month. Both editors pick months (calendar zoomed to
    // 'year'), so a value is always some day-1; periodRange() widens the end
    // to the last second of its month.
    // Initialised here, not in ngAfterViewInit: the header template and the
    // threshold getters read the range on the very first change detection.
    rangeFrom: Date = new Date(
        new Date().getFullYear(),
        new Date().getMonth(),
        1,
    );
    rangeTo: Date = this.rangeFrom;
    // Month pickers: the calendar opens on months and cannot zoom into days.
    readonly monthPickerOptions = {
        maxZoomLevel: 'year',
        minZoomLevel: 'decade',
    } as const;

    // The calendar year the threshold panel reports: the one the range ends
    // in. A range that straddles New Year shows the later year, which is the
    // one still open to act on.
    get selectedYear(): number {
        return this.rangeTo.getFullYear();
    }

    selectedBasis: PeriodBasis = 'settlementDate';
    // What the loaded figures were actually asked for. The editors above are
    // live and can move without a reload; anything describing the loaded
    // figures reads these, not the editors.
    loadedYear = this.selectedYear;
    loadedBasis: PeriodBasis = this.selectedBasis;
    selectedStoreIds: StoreId[] = [];
    selectedEntityIds: EntityId[] = [];

    isLoading = false;
    isSyncing = false;
    isVerifying = false;
    isEditorsDirty = true;
    // Null until a check has run. Distinguishing "not checked" from "checked
    // and clean" matters: a blank panel must never read as a tick.
    verifyReports: FinanceVerifyReport[] | null = null;
    // The period the check ran on, read from the pickers at that moment —
    // which need not be the period the grid below is showing.
    verifyPeriodLabel = '';
    verifyRows: VerifyRow[] = [];
    verifyAllOk = false;

    // One load, assigned together in applyLoaded() and never piecemeal: rows
    // from one period beside a summary from another made gridTruncated
    // compare the two and wave a truncated export through.
    dataSource: FinanceOrderRecord[] = [];
    summary: FinanceSummary | null = null;
    // The same period and filters counted on settlementDate, which every row
    // carries. Compared against `summary` it reveals what the selected basis
    // is silently dropping — a row with no tax point is excluded by the store
    // rather than shown as zero, so without this the grid just looks empty.
    coverage: FinanceSummary | null = null;
    // Derived from the three above once per load, not per change detection:
    // every grid hover is a pass, and the projection reads the clock.
    thresholdRows: ThresholdRow[] = [];
    basisGaps: BasisGap[] = [];
    basisGapTotal = 0;
    // The basis may be hiding rows and we cannot say: the coverage call
    // failed, or the Lambda predates the null-date counts. Shown as its own
    // warning — no banner must never read as "nothing hidden".
    basisGapsUnknown = false;
    syncReports: FinanceSyncStoreReport[] = [];

    constructor() {
        effect(() => {
            if (this.menuInHeader()) {
                this.headerContentService.set(this.headerMenu);
            } else {
                this.headerContentService.clear(this.headerMenu);
            }
        });
    }

    ngAfterViewInit(): void {
        this.load();
        this.cdref.detectChanges();
    }

    // Every subscription here ends with the page (takeUntilDestroyed). A sync
    // left running after navigation was invisible to the next instance of
    // this page, whose own isSyncing starts false — so a second sync could be
    // started over the same months, and one sync is already the platforms'
    // whole concurrency budget. Months that finished are written; say that
    // the rest were not, rather than let a half-done range look done.
    ngOnDestroy(): void {
        this.headerContentService.clear(this.headerMenu);
        if (this.isSyncing) {
            this.notificationService.pushMessage({
                message:
                    'ออกจากหน้าระหว่างซิงก์ — หยุดซิงก์แล้ว เดือนที่เสร็จก่อนหน้านี้ถูกบันทึกไว้ เดือนที่เหลือต้องซิงก์ใหม่',
                messageType: MessageType.warning,
                displayTime: 10000,
            });
        }
    }

    onEditorValueChanged(): void {
        this.isEditorsDirty = true;
    }

    // Keeps the range a range: an end moved before the start drags the start
    // back with it, and vice versa, rather than leaving an inverted pair for
    // load() to reject. Both editors hold day-1 dates, so comparing them is
    // comparing months.
    onRangeChanged(edge: 'from' | 'to'): void {
        if (this.rangeFrom && this.rangeTo && this.rangeFrom > this.rangeTo) {
            if (edge === 'from') {
                this.rangeTo = this.rangeFrom;
            } else {
                this.rangeFrom = this.rangeTo;
            }
        }
        this.isEditorsDirty = true;
    }

    // --- Derived display ---------------------------------------------------

    get storeRollups(): FinanceStoreRollup[] {
        return this.summary?.byStore ?? [];
    }

    get entityRollups(): FinanceEntityRollup[] {
        return this.summary?.byEntity ?? [];
    }

    get totalBuyerTotal(): number {
        return _sumBy(this.entityRollups, 'buyerTotal');
    }

    get totalTaxable(): number {
        return _sumBy(
            this.entityRollups,
            (row) => row.taxableConsideration ?? row.buyerTotal,
        );
    }

    get totalFees(): number {
        return _sumBy(this.entityRollups, 'feeTotal');
    }

    get totalSettlement(): number {
        return _sumBy(this.entityRollups, 'netSettlement');
    }

    get totalOutputVat(): number {
        return _sumBy(this.entityRollups, 'vatOutput');
    }

    // "After returns" companions to the tiles. Secondary by design: the
    // primary figures stay before returns, because a refund only reduces
    // output VAT once a credit note is issued, and these say what that
    // would leave. null until the Lambda sends refund totals.
    get totalRefunds(): number | null {
        return this.entityRollups.some((row) => row.refunds === undefined)
            ? null
            : round2(_sumBy(this.entityRollups, (row) => row.refunds ?? 0));
    }

    get totalTaxableAfterRefunds(): number | null {
        const refunds = this.totalRefunds;
        return refunds === null ? null : round2(this.totalTaxable - refunds);
    }

    get totalOutputVatAfterRefunds(): number | null {
        if (
            this.entityRollups.some((row) => row.refundVatOutput === undefined)
        ) {
            return null;
        }
        return round2(
            this.totalOutputVat -
                _sumBy(this.entityRollups, (row) => row.refundVatOutput ?? 0),
        );
    }

    get totalInputVatOnFees(): number {
        return _sumBy(this.entityRollups, 'feeVatInput');
    }

    // The number that tests the VAT plan's §0.5 claim that "commission is
    // neutral to the ×1.07 uplift". Commission is — but seller-funded free
    // shipping is not, and it moves this by several points.
    get effectiveFeePct(): number {
        const gross = this.totalBuyerTotal;
        return gross === 0 ? 0 : (this.totalFees / gross) * 100;
    }

    effectiveFeePctFor(rollup: FinanceStoreRollup): number {
        return rollup.buyerTotal === 0
            ? 0
            : (rollup.feeTotal / rollup.buyerTotal) * 100;
    }

    // The store's VAT base; buyerTotal only for a Lambda too old to sum it.
    taxableFor(rollup: FinanceStoreRollup): number {
        return rollup.taxableConsideration ?? rollup.buyerTotal;
    }

    // The records hold epoch seconds; the grid is handed real Dates. A cell
    // template alone only fixes what is drawn — the filter row, the header
    // filter and the Excel export all read the column value, and saw raw
    // seconds (1970, or a bare number in the .xlsx).
    readonly taxPointAsDate = (row: FinanceOrderRecord): Date | null =>
        row.taxPointDate ? new Date(row.taxPointDate * 1000) : null;

    readonly settlementAsDate = (row: FinanceOrderRecord): Date | null =>
        row.settlementDate ? new Date(row.settlementDate * 1000) : null;

    storeTitle(storeId: string): string {
        return (
            STORES.find((store) => store.storeId === storeId)?.title ?? storeId
        );
    }

    // Per-taxpayer calendar-year revenue against ฿1.8M (VAT plan §4.16).
    // Warns at ฿1.5M rather than at the threshold: crossing ฿1.8M starts a
    // 30-day clock on a registration that takes weeks, so a warning at the
    // line itself arrives already too late to act on.
    private buildThresholdRows(
        summary: FinanceSummary,
        year: number,
    ): ThresholdRow[] {
        const yearly = summary.calendarYearByEntity ?? [];
        return yearly.map((row) => {
            // The year aggregate ignores the entity filter, so a row stamped
            // with an entity this build does not know still arrives here.
            // Shown under its raw id: a throw here would fail the whole
            // load, and hiding the row would hide revenue.
            const entity = ALL_ENTITIES.find((e) => e.id === row._id);
            // Taxable consideration, not what buyers paid: platform-funded
            // vouchers and coins are the seller's revenue too, and leaving
            // them out put the bar 3.4% short of the real position.
            const revenue = row.taxableConsideration ?? row.buyerTotal ?? 0;
            const projection = projectYearEnd(year, revenue, row.months);
            const projected = projection.value;
            const state: ThresholdRow['state'] =
                revenue >= VAT_THRESHOLD_THB
                    ? 'over'
                    : revenue >= VAT_THRESHOLD_WARN_THB ||
                        projected >= VAT_THRESHOLD_THB
                      ? 'warn'
                      : 'ok';
            return {
                entityId: row._id,
                legalName: entity?.legalName ?? String(row._id),
                vatRegistered: entity?.vatRegistered ?? false,
                revenue,
                afterRefunds:
                    row.refunds === undefined
                        ? null
                        : round2(revenue - row.refunds),
                projected,
                projectionNote: projectionNote(projection),
                months: monthFigures(year, row.months, projection),
                pctOfThreshold: Math.min(
                    100,
                    (revenue / VAT_THRESHOLD_THB) * 100,
                ),
                state,
            };
        });
    }

    // Stores holding rows in this period that the selected basis cannot show,
    // because those rows have no date of that kind. Rendered as a banner: the
    // alternative is a grid that quietly omits them and reads as "no sales".
    //
    // Counted by the Lambda as rows whose date of the chosen kind is null,
    // within the settlement window. It used to be the difference between
    // the settlement count and the tax-point count of the same window,
    // which also swept in every order created in one month and paid in the
    // next — those are shown under their own month, not missing — and, once
    // Lazada and TikTok rows became per-payout slices, every fee-only slice
    // of an earlier sale as well. A banner that says rows have no date must
    // count rows with no date.
    //
    // `unknown` when that count is not to be had — no coverage, or a Lambda
    // too old to send it. Defaulting the missing count to zero said "nothing
    // hidden" on exactly the loads where nobody had looked.
    private buildBasisGaps(
        coverage: FinanceSummary | null,
        basis: PeriodBasis,
    ): { gaps: BasisGap[]; unknown: boolean } {
        if (basis === 'settlementDate') {
            return { gaps: [], unknown: false };
        }
        if (!coverage) {
            return { gaps: [], unknown: true };
        }
        const rows = coverage.byStore.map((row) => ({
            storeId: row._id,
            stored: row.rows ?? row.orders,
            hidden:
                basis === 'taxPointDate' ? row.nullTaxPoint : row.nullOrderDate,
        }));
        return {
            gaps: rows
                .map((row) => ({ ...row, hidden: row.hidden ?? 0 }))
                .filter((row) => row.hidden > 0),
            unknown: rows.some((row) => row.hidden === undefined),
        };
    }

    // Per-store verdict: does what we hold match what the platform says the
    // period contains?
    //
    // Compared on SETTLEMENT, always, whatever basis the grid is showing. The
    // platforms index by their own payout dates, so comparing a tax-point view
    // against a settlement-indexed probe would show a difference that is real
    // but means nothing about completeness.
    private buildVerifyRows(
        reports: FinanceVerifyReport[],
        storedSummary: FinanceSummary,
    ): VerifyRow[] {
        const storedBy = new Map(
            storedSummary.byStore.map((row) => [row._id, row]),
        );
        return reports.map((report): VerifyRow => {
            const stored = storedBy.get(report.storeId);
            const storedOrders = stored?.orders ?? 0;
            const storedMoney = stored?.netSettlement ?? 0;
            const base = {
                storeId: report.storeId,
                title: report.title,
                stored: storedOrders,
                storedMoney,
                platform: null,
                platformMoney: null,
            };

            if (report.error) {
                return { ...base, state: 'error', detail: report.error };
            }

            // Order count is the stronger signal, so prefer it. TikTok has no
            // per-order index, so money is all it can offer.
            if (report.platformOrders !== null) {
                const diff = storedOrders - report.platformOrders;
                return {
                    ...base,
                    platform: report.platformOrders,
                    platformMoney: report.platformSettlement,
                    state: diff === 0 ? 'ok' : diff < 0 ? 'short' : 'over',
                    detail:
                        diff === 0
                            ? 'ครบ'
                            : diff < 0
                              ? `ขาด ${Math.abs(diff)} รายการ`
                              : `เกิน ${diff} รายการ`,
                };
            }

            if (report.platformSettlement !== null) {
                // Money only — TikTok has no per-order index, so the settled
                // total is the strongest like-for-like figure available.
                //
                // 1 THB tolerance covers satang rounding across hundreds of
                // rows; it is not slack for a real gap. An apparent 3,345 THB
                // shortfall here turned out to be TikTok returning statements
                // from outside the requested window, which the probe now
                // filters out — so this can be scored rather than excused.
                const diff = round2(storedMoney - report.platformSettlement);
                return {
                    ...base,
                    platformMoney: report.platformSettlement,
                    state:
                        Math.abs(diff) <= 1
                            ? 'ok'
                            : diff < 0
                              ? 'short'
                              : 'over',
                    detail:
                        Math.abs(diff) <= 1
                            ? 'ยอดเงินตรงกัน'
                            : `ต่างกัน ${diff.toLocaleString()} บาท`,
                };
            }

            return { ...base, state: 'unknown', detail: 'ตรวจสอบไม่ได้' };
        });
    }

    // Documents the period holds, counted server-side over the same filter
    // as the grid query.
    get totalRows(): number {
        return (this.summary?.byStore ?? []).reduce(
            (sum, row) => sum + (row.rows ?? row.orders),
            0,
        );
    }

    get gridTruncated(): boolean {
        return this.summary !== null && this.dataSource.length < this.totalRows;
    }

    // Rows whose fee taxonomy did not reproduce the platform's own settlement,
    // or that arrived with no tax point. Both make the totals above less
    // trustworthy than they look, so they are shown rather than logged.
    get syncWarnings(): FinanceSyncStoreReport[] {
        return this.syncReports.filter(
            (report) =>
                report.error ||
                report.reconciliationFailures > 0 ||
                report.missingTaxPoint > 0 ||
                report.duplicateIds > 0 ||
                (report.unjoinedOrders ?? 0) > 0 ||
                (report.unparsedAmounts ?? 0) > 0 ||
                (report.unmappedFeeNames?.length ?? 0) > 0,
        );
    }

    // --- Actions -----------------------------------------------------------

    load(): void {
        if (this.isLoading || this.isSyncing || this.isVerifying) {
            return;
        }
        const range = this.periodRange();
        // A verdict is about one period as it stood; after a reload it would
        // sit above figures it was never checked against.
        this.clearVerdict();
        this.beginLoading();
        const query = {
            periodBasis: this.selectedBasis,
            from: range.from.unix(),
            to: range.to.unix(),
            entityIds: this.selectedEntityIds.length
                ? this.selectedEntityIds
                : undefined,
            storeIds: this.selectedStoreIds.length
                ? this.selectedStoreIds
                : undefined,
        };
        const year = this.selectedYear;
        // Together, and applied together. Run one after another, a summary
        // that failed after the rows had landed left a new period's grid
        // under the old period's summary. A failure now leaves the previous
        // load on screen, whole.
        forkJoin({
            records: this.financeTxService.query(query),
            summary: this.financeTxService.summary(query, year),
            coverage: this.loadCoverage(query),
        })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: ({ records, summary, coverage }) => {
                    this.dataSource = records;
                    this.summary = summary;
                    this.coverage = coverage;
                    this.loadedYear = summary.thresholdYear ?? year;
                    this.loadedBasis = query.periodBasis;
                    this.thresholdRows = this.buildThresholdRows(
                        summary,
                        this.loadedYear,
                    );
                    const { gaps, unknown } = this.buildBasisGaps(
                        coverage,
                        query.periodBasis,
                    );
                    this.basisGaps = gaps;
                    this.basisGapTotal = _sumBy(gaps, 'hidden');
                    this.basisGapsUnknown = unknown;
                    this.isEditorsDirty = false;
                    this.endLoading();
                },
                error: (error) => this.fail(error),
            });
    }

    // One extra call, on settlementDate, purely to know what the selected
    // basis is hiding. Skipped when settlement IS the basis, since then the
    // two are the same query. A failure here must not fail the load — the
    // figures themselves are fine — so it resolves to null, which
    // buildBasisGaps reports as "unknown" rather than as "nothing hidden".
    private loadCoverage(
        query: FinancePeriodQuery,
    ): Observable<FinanceSummary | null> {
        if (query.periodBasis === 'settlementDate') {
            return of(null);
        }
        return this.financeTxService
            .summary({ ...query, periodBasis: 'settlementDate' })
            .pipe(
                catchError((error) => {
                    console.error(error);
                    return of(null);
                }),
            );
    }

    // Pulls every month in the selected range from every platform, one month
    // after another, writing each as soon as it is pulled.
    //
    // Month by month rather than the range in one go: one month is already
    // the platforms' full concurrency budget, and a browser closed mid-way
    // then keeps the months that landed. A month that fails is named and
    // skipped, not fatal — a range missing one month is obviously wrong on
    // the threshold panel; a range abandoned halfway just looks quiet.
    syncNow(): void {
        if (this.isLoading || this.isSyncing || this.isVerifying) {
            return;
        }
        const months = this.monthsInRange();
        if (months.length > SYNC_MAX_MONTHS) {
            this.notificationService.pushMessage({
                message: `ซิงก์ได้ครั้งละไม่เกิน ${SYNC_MAX_MONTHS} เดือน (เลือกไว้ ${months.length} เดือน)`,
                messageType: MessageType.error,
            });
            return;
        }
        const totals = { inserted: 0, matched: 0, modified: 0 };
        const drift: FinanceUpsertResult['entityDrift'] = [];
        const reports: FinanceSyncStoreReport[] = [];
        const failed: string[] = [];
        let records = 0;

        this.isSyncing = true;
        from$(months)
            .pipe(
                concatMap((ym, index) => {
                    this.dataGrid.instance.beginCustomLoading(
                        months.length === 1
                            ? 'กำลังดึงข้อมูลจากแพลตฟอร์ม…'
                            : `กำลังซิงก์ ${monthLabel(ym)} (${index + 1}/${months.length})…`,
                    );
                    const range = this.monthRange(ym.year, ym.month);
                    return this.syncPeriod(range.from, range.to).pipe(
                        map((result) => {
                            totals.inserted += result.upsert.inserted;
                            totals.matched += result.upsert.matched;
                            totals.modified += result.upsert.modified;
                            drift.push(...result.upsert.entityDrift);
                            records += result.records;
                            // Tagged with the month so the warnings panel
                            // can say which one, not just which store.
                            reports.push(
                                ...result.reports.map((report) => ({
                                    ...report,
                                    title:
                                        months.length === 1
                                            ? report.title
                                            : `${report.title} · ${monthLabel(ym)}`,
                                })),
                            );
                            return true;
                        }),
                        catchError((error) => {
                            console.error(
                                `sync ${monthKey(ym.year, ym.month)} failed`,
                                error,
                            );
                            failed.push(monthLabel(ym));
                            return of(false);
                        }),
                    );
                }),
                toArray(),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe({
                next: () => {
                    this.isSyncing = false;
                    this.dataGrid.instance.endCustomLoading();
                    this.syncReports = reports;
                    // A store that fails does not fail its month — the sync
                    // service reports it and carries on — so a month can
                    // "succeed" having fetched nothing. Counted here, or the
                    // toast calls a month nobody could read an empty one.
                    const storeErrors = reports.filter(
                        (report) => report.error,
                    ).length;
                    const partial = failed.length > 0 || storeErrors > 0;
                    if (storeErrors) {
                        this.notificationService.pushMessage({
                            message: `⚠ ดึงข้อมูลไม่สำเร็จ ${storeErrors} ${months.length === 1 ? 'ร้าน' : 'ร้าน·เดือน'} — ดูรายละเอียดด้านบนแล้วซิงก์ซ้ำ`,
                            messageType: MessageType.error,
                            displayTime: 10000,
                        });
                    }
                    if (failed.length) {
                        this.notificationService.pushMessage({
                            message:
                                `⚠ ซิงก์ไม่สำเร็จ ${failed.length} เดือน: ${failed.join(' ')}` +
                                ' — เลือกเดือนนั้นแล้วซิงก์ซ้ำ',
                            messageType: MessageType.error,
                            displayTime: 10000,
                        });
                    }
                    if (failed.length === months.length) {
                        // Nothing landed; the failure message above is the
                        // whole story and a "สำเร็จ" beside it would be false.
                    } else if (records === 0) {
                        // "Nothing there" is only a finding when every store
                        // and month was actually read.
                        if (!partial) {
                            this.notificationService.pushMessage({
                                message: 'ไม่พบรายการในช่วงเวลานี้',
                                messageType: MessageType.warning,
                            });
                        }
                    } else {
                        this.notifyUpsert(
                            { ...totals, entityDrift: drift },
                            partial,
                        );
                    }
                    this.load();
                },
                error: (error) => {
                    this.isSyncing = false;
                    this.dataGrid.instance.endCustomLoading();
                    this.fail(error);
                },
            });
    }

    // One period: pull from every platform, then write. Shared by the
    // single-month and whole-year actions so "a sync" cannot come to mean two
    // different things. An empty pull still resolves (upsert of nothing is a
    // no-op) so the caller can tell "nothing there" from "failed".
    private syncPeriod(
        from: moment.Moment,
        to: moment.Moment,
    ): Observable<{
        reports: FinanceSyncStoreReport[];
        upsert: FinanceUpsertResult;
        records: number;
    }> {
        return this.financeSyncService.sync(from, to).pipe(
            mergeMap((result) =>
                this.financeTxService.upsert(result.records).pipe(
                    map((upsert) => ({
                        reports: result.reports,
                        upsert,
                        records: result.records.length,
                    })),
                ),
            ),
        );
    }

    private notifyUpsert(upsert: FinanceUpsertResult, partial: boolean): void {
        // Entity drift is never applied automatically — a store that changed
        // taxpayer must be looked at, not silently restamped across
        // historical rows.
        if (upsert.entityDrift.length > 0) {
            this.notificationService.pushMessage({
                message:
                    `⚠ ${upsert.entityDrift.length} รายการมีเจ้าของภาษีไม่ตรงกับที่บันทึกไว้ ` +
                    '(ไม่ได้เขียนทับ) — ตรวจสอบก่อนใช้ตัวเลข',
                messageType: MessageType.warning,
                displayTime: 8000,
            });
        }
        this.notificationService.pushMessage({
            message: `${partial ? 'ซิงก์ได้บางส่วน' : 'ซิงก์สำเร็จ'}: เพิ่ม ${upsert.inserted}, อัปเดต ${upsert.matched}`,
            messageType: partial ? MessageType.warning : MessageType.success,
        });
    }

    // Asks the platforms what the period actually contains and compares.
    //
    // Separate from Sync on purpose. Sync is slow and writes; this only reads
    // index endpoints and writes nothing, so it is cheap enough to run
    // whenever a number needs to be trusted — before filing, say — without
    // re-pulling the month.
    verifyNow(): void {
        if (this.isLoading || this.isSyncing || this.isVerifying) {
            return;
        }
        const range = this.periodRange();
        this.isVerifying = true;
        this.clearVerdict();
        const months = this.monthsInRange();
        const first = monthLabel(months[0]);
        const last = monthLabel(months[months.length - 1]);
        this.verifyPeriodLabel = first === last ? first : `${first} – ${last}`;
        this.dataGrid.instance.beginCustomLoading('กำลังตรวจสอบกับแพลตฟอร์ม…');
        // The stored side is fetched here: every store, on settlementDate,
        // for the same range the probe gets. Not the page's `summary` or
        // `coverage` — those carry the store/entity filters and the selected
        // basis, and the probe knows neither, so a filtered-out store scored
        // as holding nothing and read as short. If it fails the check fails:
        // there is no honest fallback to score a settlement-indexed probe
        // against.
        forkJoin({
            reports: this.financeSyncService.verify(range.from, range.to),
            stored: this.financeTxService.summary({
                periodBasis: 'settlementDate',
                from: range.from.unix(),
                to: range.to.unix(),
            }),
        })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: ({ reports, stored }) => {
                    this.verifyReports = reports;
                    this.verifyRows = this.buildVerifyRows(reports, stored);
                    const bad = this.verifyRows.filter(
                        (row) => !verifyRowPasses(row),
                    ).length;
                    this.verifyAllOk = this.verifyRows.length > 0 && bad === 0;
                    this.isVerifying = false;
                    this.dataGrid.instance.endCustomLoading();
                    this.notificationService.pushMessage({
                        message: bad
                            ? `พบความไม่ตรงกัน ${bad} ร้าน — ดูรายละเอียดด้านบน`
                            : 'ข้อมูลครบตรงกับแพลตฟอร์มทุกร้าน',
                        messageType: bad
                            ? MessageType.warning
                            : MessageType.success,
                        displayTime: bad ? 8000 : 3000,
                    });
                },
                error: (error) => {
                    this.isVerifying = false;
                    this.dataGrid.instance.endCustomLoading();
                    this.fail(error);
                },
            });
    }

    onExporting(): void {
        // A partial export of a tax figure is worse than none: it opens in
        // Excel looking complete.
        if (this.gridTruncated) {
            this.notificationService.pushMessage({
                message: `ตารางแสดงไม่ครบ (${this.dataSource.length} จาก ${this.totalRows} แถว) — ลดช่วงเดือนหรือกรองร้านก่อน Export`,
                messageType: MessageType.error,
                displayTime: 8000,
            });
            return;
        }
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet('Revenue & Fees');
        exportDataGrid({
            component: this.dataGrid.instance,
            worksheet,
            autoFilterEnabled: true,
            customizeCell: ({ gridCell, excelCell }: any) => {
                if (
                    gridCell?.rowType !== 'data' &&
                    gridCell?.rowType !== 'totalFooter'
                ) {
                    return;
                }
                const format = EXPORT_NUM_FORMATS[gridCell?.column?.dataField];
                if (format) {
                    excelCell.numFmt = format;
                    // From the grid's value, not the Excel cell's: for a
                    // footer total the exporter has already put the display
                    // text there, and Number('1,234,567.89') is NaN — every
                    // total of a thousand or more stayed a text cell that
                    // SUMs to zero.
                    const value = Number(gridCell.value);
                    if (gridCell.value != null && Number.isFinite(value)) {
                        excelCell.value = value;
                    }
                }
            },
        })
            .then(() => workbook.xlsx.writeBuffer())
            .then((buffer) => {
                FileSaver.saveAs(
                    new Blob([buffer], { type: 'application/octet-stream' }),
                    `${this.reportName()}.xlsx`,
                );
            })
            .catch((error) => {
                console.error(error);
                this.notificationService.pushMessage({
                    message: this.utilService.errorToString(error),
                    messageType: MessageType.error,
                });
            });
    }

    // --- Helpers -----------------------------------------------------------

    // First second of the first month to last second of the last.
    private periodRange(): { from: moment.Moment; to: moment.Moment } {
        return {
            from: moment(this.rangeFrom).startOf('month'),
            to: moment(this.rangeTo).endOf('month'),
        };
    }

    private monthsInRange(): YearMonth[] {
        const months: YearMonth[] = [];
        const cursor = moment(this.rangeFrom).startOf('month');
        const last = moment(this.rangeTo).startOf('month');
        while (!cursor.isAfter(last)) {
            months.push({ year: cursor.year(), month: cursor.month() + 1 });
            cursor.add(1, 'month');
        }
        return months;
    }

    private monthRange(
        year: number,
        month: number,
    ): { from: moment.Moment; to: moment.Moment } {
        const from = moment({ year, month: month - 1, day: 1 }).startOf('day');
        return { from, to: from.clone().endOf('month') };
    }

    private reportName(): string {
        const months = this.monthsInRange();
        const first = months[0];
        const last = months[months.length - 1];
        const span =
            months.length === 1
                ? monthKey(first.year, first.month)
                : `${monthKey(first.year, first.month)} ถึง ${monthKey(last.year, last.month)}`;
        return `รายได้และค่าธรรมเนียม ${span}`;
    }

    private clearVerdict(): void {
        this.verifyReports = null;
        this.verifyRows = [];
        this.verifyAllOk = false;
    }

    private fail(error: unknown): void {
        console.error(error);
        this.endLoading();
        this.notificationService.pushMessage({
            message: this.utilService.errorToString(error),
            messageType: MessageType.error,
        });
    }

    private beginLoading(): void {
        this.isLoading = true;
        this.dataGrid.instance.beginCustomLoading('');
    }

    private endLoading(): void {
        this.isLoading = false;
        this.dataGrid.instance.endCustomLoading();
    }
}
